// Guided tips (the "guided first game"): one short coach mark at a time, shown the first time a
// situation comes up and pointing at the thing it explains. Each is seen once; all can be turned off.
// They explain *how to act*, never *what to do* — strategy is the player's to discover.
import { useLayoutEffect, useRef, useState } from 'react';
import type { AvailableActions, PlayerView } from '@bcm/rules';
import { useGame } from '../state/store';
import { TAP, TOUCH } from './Tooltip';

interface Step {
  id: string;
  /** CSS selector of the element the tip points at (`null` = the board, top centre). */
  target: string | null;
  title: string;
  text: string;
  when: (v: PlayerView, a: AvailableActions | null, me: number) => boolean;
}

const STEPS: Step[] = [
  {
    id: 'place',
    target: null,
    title: 'Your turn',
    text: TOUCH
      ? 'Tap a lit room to see what it does, then tap it again to send an Assistant there. Tap anything else for details.'
      : 'Send an Assistant to a lit room by clicking it. Hover any room — or anything else — for details.',
    when: (v, a) => !!a?.free && a.canPlace && !v.turn?.basePlaced,
  },
  {
    id: 'permits',
    target: '.permit-grid',
    title: 'Permits',
    text: 'Spells are cast with permits. Each Department hands out different colours.',
    when: (v, _a, me) => Object.values(v.seats[me]!.permits).some((n) => n > 0),
  },
  {
    id: 'cast',
    target: '.tray-group.spells',
    title: 'Casting',
    text: `${TAP} a spell in your hand to cast it once you hold its permits. Cast spells score VP and keep working.`,
    when: (v, a) => !!a?.free && !!v.turn?.basePlaced,
  },
  {
    id: 'end',
    target: '.end-turn',
    title: 'Ending your turn',
    text: 'Done for now? End your turn here (or press Space).',
    when: (_v, a) => !!a?.free && !!a.canEndTurn,
  },
  {
    id: 'arcanium',
    target: null,
    title: 'The Arcanium',
    text: `${TAP} one of the spells on the table to buy it with mana, or skip. Don't like what's on display? Pay 1 mana to refresh it first.`,
    when: (_v, a) => a?.decisions[0]?.kind === 'buySpell',
  },
  {
    id: 'planning',
    target: '.tray-group.actions',
    title: 'Playing Action cards',
    text: `${TAP} the lit Action cards in your hand to play them. Press Done when you are finished.`,
    when: (_v, a) => a?.decisions[0]?.kind === 'playAction',
  },
  {
    id: 'auditor',
    target: null,
    title: 'City Problems',
    text: `${TAP} a lit City Problem on the table to claim it. The first player to claim one scores the most.`,
    when: (_v, a) => a?.decisions[0]?.kind === 'claimProblem',
  },
  // City Resolutions and Annex rooms (P-17, P-18): explained the first time they matter.
  {
    id: 'resolution',
    target: '.resolutions-rail',
    title: 'Resolutions',
    text: 'The first claim on a City Problem passes its Resolution: one rule changes for every player, from the next round on. Hover a plaque to read it.',
    when: (v) => (v.resolutions?.queued.length ?? 0) + (v.resolutions?.inForce.length ?? 0) > 0,
  },
  {
    id: 'annex',
    target: null,
    title: 'The Annex',
    text: 'A Resolution opened an Annex room at the corner of the Ministry. It works like any room — send an Assistant there. With both bays full, the older room closes.',
    when: (v) => (v.annex?.bays ?? []).some(Boolean),
  },
  {
    id: 'memo',
    target: '.tray-group.actions',
    title: 'A free memo',
    text: `Memos Fly Free: once a round, ${TAP.toLowerCase()} an Action card in your hand to play it right away, anywhere.`,
    when: (_v, a) => !!a?.free && (a.freeActions ?? []).some((f) => f.legal),
  },
  {
    id: 'rivals',
    target: '.rivals-bar',
    title: 'Your rivals',
    text: `Open a rival’s Spells to see what they have cast; ${TAP.toLowerCase()} their name for the full dossier.`,
    when: (v, a, me) => !a?.free && v.phase === 'action' && v.activeSeat !== me && v.round >= 1,
  },
];

export function Coach({ view, actions }: { view: PlayerView; actions: AvailableActions | null }) {
  const me = useGame((s) => s.you?.seat);
  const busy = useGame((s) => s.busy);
  const coach = useGame((s) => s.settings.coach);
  const seen = useGame((s) => s.settings.coachSeen);
  const setSettings = useGame((s) => s.setSettings);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; arrow: 'down' | 'up' | 'left' | 'right' | 'none' }>({ left: -9999, top: -9999, arrow: 'none' });

  const step = coach && me != null && !busy && view.phase === 'action' ? STEPS.find((s) => !seen.includes(s.id) && s.when(view, actions, me)) : undefined;

  useLayoutEffect(() => {
    if (!step || !ref.current) return;
    const el = ref.current;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const t = step.target ? document.querySelector(step.target)?.getBoundingClientRect() : null;
    // Never cover the decision bar or the tray: board-level tips sit just above them.
    const floor = Math.min(document.querySelector('.decision-bar')?.getBoundingClientRect().top ?? innerHeight, document.querySelector('.tray-hands')?.getBoundingClientRect().top ?? innerHeight);
    if (!t) {
      setPos({ left: innerWidth / 2 - w / 2, top: floor - h - 14, arrow: 'none' });
      return;
    }
    if (t.left > innerWidth * 0.7) setPos({ left: t.left - w - 16, top: Math.max(70, t.top + 20), arrow: 'right' });
    else if (t.right < innerWidth * 0.3) setPos({ left: t.right + 16, top: Math.max(70, t.top + 10), arrow: 'left' });
    else {
      const bar = document.querySelector('.decision-bar')?.getBoundingClientRect();
      const above = bar && bar.top < t.top ? bar.top : t.top;
      setPos({ left: Math.max(12, Math.min(innerWidth - w - 12, t.left + t.width / 2 - w / 2)), top: above - h - 16, arrow: bar && bar.top < t.top ? 'none' : 'down' });
    }
  }, [step?.id, actions?.decisions[0]?.id]);

  if (!step) return null;
  const dismiss = () => setSettings({ coachSeen: [...seen, step.id] });
  return (
    <div ref={ref} className={`coach frame arrow-${pos.arrow}`} style={{ left: pos.left, top: pos.top }} role="note" aria-live="polite">
      <div className="coach-title">{step.title}</div>
      <div className="coach-text">{step.text}</div>
      <div className="coach-actions">
        <button className="small ghost" onClick={() => setSettings({ coach: false })}>
          Turn off tips
        </button>
        <button className="gilt-btn small" onClick={dismiss}>
          Got it
        </button>
      </div>
    </div>
  );
}
